import { keepPreviousData, useQuery } from "@tanstack/react-query";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Grid, Loader, Message, Segment } from "semantic-ui-react";

import { getMeanComparisonForAucAsync, getMeanComparisonForTimecourseAsync } from "../../../apis/statisticsApi";
import type {
	FlightStats,
	PlotAnalysisResultsForTrialTimecourse,
	TimecourseAnalysisData,
} from "../../../apis/types/analysisTypes";
import type { AnalysisType, TrialAnalysisDataOptions, TrialData } from "../../../apis/types/trialTypes";
import { useAuth0 } from "../../../auth/auth0";
import { useUserAuth } from "../../../hooks/useUserAuth";
import { useTrialAnalysis, useTrialAnalysisDispatch } from "../../Content/Trials/TrialAnalysis/TrialAnalysisContext";
import AnalysisTopBar from "../AnalysisTopBar";

import { DateTime, Interval } from "luxon";
import "./styles.css";
import AreaUnderTheCurveTableNew from "../../Content/Widgets/AreaUnderTheCurveTable/indexNew";
import TimecourseLineChart from "../../Content/Widgets/Charts/TimecourseLineChart";
import TimecourseBoxWhiskerChart from "../../Content/Widgets/Charts/TimecourseBoxWhiskerChart";
import type { AerialPlotResponse, Message as ApiMessage } from "../../../apis/types";
import type { TimecourseDataViewModel } from "../../../apis/types/statisticsTypes";

interface TreatmentCLD {
	trialTreatmentId: number;
	letter: string;
	mean?: string;
	cv?: string;
	pValue?: string;
}

interface MeanComparisonDataForTimecourse {
	flightId: string;
	analysisId: string;
	treatments: TreatmentCLD[];
}

interface MeanComparisonDataForTimecourseResponse {
	letters: MeanComparisonDataForTimecourse[];
	pValue: string;
	fallbackUsed: boolean;
	fallbackReason: string;
	flightStats: FlightStats[];
	anovaTable: AnovaTableRowForTimecourse[];
}

interface AnovaTableRowForTimecourse {
	term: string;
	numDF: number;
	denDF: number;
	fValue: number;
	pValue: number;
}

interface MeanComparisonDataForAuc {
	letteringPairs: { treatmentId: number; letter: string }[];
	cv: string;
	mean: string;
	pValue: string;
	trialId?: string;
}

type Props = {
	data: PlotAnalysisResultsForTrialTimecourse[];
	analysisData?: TimecourseAnalysisData;
	dataLoading: boolean;
	trialDataResults: TrialData;
	trialOptions: TrialAnalysisDataOptions;
	gduList?: { day: string; gdu: number; monthDay: string }[];
};

const TimecourseComponentNew = ({
	data,
	analysisData,
	dataLoading,
	trialDataResults,
	trialOptions,
	gduList,
}: Props) => {
	const { committed, pending, display } = useTrialAnalysis();
	const { changeSettings } = useTrialAnalysisDispatch();
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();

	// --- Derived ---

	const orderedFlights = useMemo(() => {
		const flights = _.chain(data)
			.sortBy((d) => DateTime.fromISO(d.flightDate!).toMillis())
			.sortedUniqBy((d) => d.flightId)
			.map((d) => {
				const gdu = gduList
					?.slice(
						0,
						gduList.findIndex((item) => DateTime.fromISO(item.day).hasSame(DateTime.fromISO(d.flightDate!), "day")),
					)
					.reduce((a, b) => a + b.gdu, 0);
				return {
					flightId: d.flightId!,
					flightDate: d.flightDate!,
					flightGdus: gdu && Math.round(gdu),
					isPlantFlight: d.flightId! === analysisData?.plantFlightId,
					isHarvestFlight: d.flightId! === analysisData?.harvestFlightId,
				};
			})
			.value();
		return flights ?? [];
	}, [data, gduList, analysisData?.plantFlightId, analysisData?.harvestFlightId]);

	const analysisName = useMemo(
		() =>
			_.chain(trialOptions?.trialAnalysisOptions)
				.flatMap<AnalysisType>("analysisTypes")
				.find((at) => at.id === pending.analysisId)
				.value()?.name ?? "",
		[trialOptions, pending.analysisId],
	);

	const hasNoData = analysisData ? analysisData.dataset.length === 0 : false;

	const canRunStats =
		!dataLoading &&
		!!display.isRCBD &&
		!!pending.analysisId &&
		!!committed.selectedFlightIds &&
		!!committed.selectedTreatmentMatrix &&
		!!data?.length &&
		!!trialDataResults &&
		!hasNoData &&
		!!committed.meanComparisonOptionId &&
		!!committed.alphaOptionId;

	// --- Helper functions ---

	const filterRawAnalysisData = useCallback(
		(input: PlotAnalysisResultsForTrialTimecourse[]) => {
			const skipOutlierFilter = !_.some(input, (rd) => rd?.isOutlier3 !== undefined || rd?.isOutlier1_5 !== undefined);

			return _.filter(input, (r) => {
				return (
					(committed.removeDamagedOrExcluded ? r.excludeFromAssessment === false : true) &&
					(skipOutlierFilter ||
						(committed.removeOutliers && committed.outlierType === 3
							? r.isOutlier3 === false
							: committed.removeOutliers && committed.outlierType === 1.5
								? r.isOutlier1_5 === false
								: true))
				);
			});
		},
		[committed.outlierType, committed.removeDamagedOrExcluded, committed.removeOutliers],
	);

	const getIsDataUnbalanced = useCallback(
		(treatmentData: PlotAnalysisResultsForTrialTimecourse[]) => {
			if (!treatmentData.length) return true;
			const skipOutlierFilter = !_.some(
				treatmentData,
				(rd) => rd?.isOutlier3 !== undefined || rd?.isOutlier1_5 !== undefined,
			);
			const filtered = _.filter(
				treatmentData,
				(rd) =>
					!(
						(committed.removeDamagedOrExcluded && rd.excludeFromAssessment === true) ||
						skipOutlierFilter ||
						(committed.removeOutliers && committed.outlierType === 3
							? rd.isOutlier3 === true && rd.isOutlier1_5 === false
							: committed.removeOutliers && committed.outlierType === 1.5
								? rd.isOutlier1_5 === true && rd.isOutlier3 === false
								: "")
					),
			);

			let unbalancedData = false;
			const groupings = [
				_.groupBy(filtered, (rd) => `${rd.trialTreatmentId}|${rd.plotReplicate}|${rd.flightId}`),
				_.groupBy(filtered, (rd) => `${rd.trialTreatmentId}|${rd.flightId}`),
				_.groupBy(filtered, (rd) => `${rd.plotReplicate}|${rd.flightId}`),
			];
			for (const grouped of groupings) {
				const len = Object.values(grouped)[0]?.length;
				if (Object.values(grouped).some((g) => g.length !== len)) {
					unbalancedData = true;
					break;
				}
			}
			return unbalancedData;
		},
		[committed.outlierType, committed.removeDamagedOrExcluded, committed.removeOutliers],
	);

	const rawFilteredData = useMemo(() => filterRawAnalysisData([...data]), [filterRawAnalysisData, data]);

	// --- useQuery: timecourse mean comparison ---

	useEffect(() => {
		if (!display.isRCBD) {
			console.warn(
				"Selected experimental design must be a Randomized Complete Block Design (RCBD) in order to run mean comparison tests",
			);
		}
	}, [display.isRCBD]);

	const timecourseRequestData = useMemo(() => {
		if (committed.selectedTreatmentMatrix === null || !orderedFlights.length) return {};

		const filtered = _.chain(rawFilteredData)
			.filter(
				(d) =>
					committed.selectedTreatmentMatrix![d.trialTreatmentId!] &&
					!!committed.selectedFlightIds?.includes(d.flightId ?? ""),
			)
			.groupBy((td) => `${td.plotGroup}_${td.plotReplicate}_${td.trialTreatmentId}_${td.flightId}`)
			.mapValues((plotGroup) => ({
				...plotGroup[0],
				plotName: plotGroup[0].plotGroup,
				plotAnalysisResultValue: _.meanBy(plotGroup, (p) => p.plotAnalysisResultValue),
			}))
			.flatMap()
			.orderBy(["flightDate"], ["asc"])
			.value();

		const firstDate = DateTime.fromISO(orderedFlights[0].flightDate);

		return {
			data: _.map(filtered, (d) => ({
				plotId: d.plotGroup ?? d.plotId,
				flightId: d.flightId,
				dayOfYear: committed.useGdu
					? orderedFlights.find((f) => f.flightId === d.flightId)!.flightGdus!
					: Interval.fromDateTimes(firstDate, DateTime.fromISO(d.flightDate!)).count("days") - 1,
				trialTreatmentId: d.trialTreatmentId,
				range: d.plotRange,
				plotReplicate: d.plotReplicate,
				value: d.plotAnalysisResultValue,
				excludeFromAssessment: d.excludeFromAssessment,
				isOutlier3: d.isOutlier3,
				isOutlier1_5: d.isOutlier1_5,
			})),
		};
	}, [
		rawFilteredData,
		committed.selectedTreatmentMatrix,
		committed.selectedFlightIds,
		committed.useGdu,
		orderedFlights,
	]);

	const {
		data: [timecourseComparisonResult, timecourseComparisonStatus],
		isFetching: isFetchingTimecourseComparison,
		error: timecourseComparisonError,
	} = useQuery({
		queryKey: [
			"run timecourse mean comparison",
			timecourseRequestData,
			pending.analysisId,
			pending.analysisTypeId,
			userAuth.currentClientId ?? "",
			committed.meanComparisonOptionId,
			committed.alphaOptionId,
			committed.removeDamagedOrExcluded,
			committed.removeOutliers,
			committed.outlierType,
			committed.anovaType,
			committed.useGdu,
		],
		queryFn: async (): Promise<[MeanComparisonDataForTimecourseResponse | undefined, number]> => {
			const accessToken = await getTokenSilently();
			const res = (await getMeanComparisonForTimecourseAsync(
				// only {} while the query is disabled
				timecourseRequestData as TimecourseDataViewModel,
				userAuth.currentClientId ?? "",
				pending.analysisId!,
				committed.meanComparisonOptionId!,
				committed.alphaOptionId!,
				accessToken,
				committed.removeDamagedOrExcluded,
				committed.removeOutliers,
				committed.outlierType,
				committed.anovaType,
				committed.useGdu,
			)) as
				| (AerialPlotResponse<MeanComparisonDataForTimecourseResponse, ApiMessage[]> & { statusCode: 200 })
				| (AerialPlotResponse<string, ApiMessage[]> & { statusCode: 400 });
			if (res.statusCode !== 200) {
				console.error(res.data);
				return [undefined, res.statusCode];
			}

			if (res.data.fallbackUsed) {
				console.warn(res.data.fallbackReason);
			}
			return [res.data, res.statusCode];
		},
		placeholderData: keepPreviousData,
		initialData: [undefined, 200],
		enabled: canRunStats,
	});

	useEffect(() => {
		if (timecourseComparisonError) {
			console.error(timecourseComparisonError);
			toast.error("Could not get mean comparison data");
		}
	}, [timecourseComparisonError]);

	// --- useQuery: AUC mean comparison ---

	const aucInput = useMemo(() => {
		const filteredByAnalysis = _.filter(
			rawFilteredData,
			(d) => d.flightId === analysisData?.plantFlightId || d.flightId === analysisData?.harvestFlightId,
		);
		return _.filter(
			filteredByAnalysis,
			(d) =>
				!!committed.selectedTreatmentMatrix?.[d.trialTreatmentId!] &&
				!!committed.selectedFlightIds?.includes(d.flightId ?? ""),
		);
	}, [
		rawFilteredData,
		committed.selectedTreatmentMatrix,
		committed.selectedFlightIds,
		analysisData?.plantFlightId,
		analysisData?.harvestFlightId,
	]);

	const isDataUnbalanced = useMemo(() => getIsDataUnbalanced(aucInput), [getIsDataUnbalanced, aucInput]);

	const aucRequestData = useMemo(
		() => ({
			data: _.chain(analysisData?.aucData)
				.flatMap((ad) => ad.plotGroupAucs)
				.filter((pga) => !!committed.selectedTreatmentMatrix?.[pga.trialTreatmentId])
				.map((d) => ({
					trialTreatmentId: d.trialTreatmentId,
					plotReplicate: d.plotReplicate,
					totalAuc: d.meanAuc,
				})),
		}),
		[analysisData?.aucData, committed.selectedTreatmentMatrix],
	);

	const {
		data: aucComparisonResult,
		isFetching: isFetchingAucComparison,
		error: aucComparisonError,
	} = useQuery({
		queryKey: [
			"run auc mean comparison",
			aucRequestData,
			pending.analysisId,
			committed.meanComparisonOptionId,
			committed.alphaOptionId,
			isDataUnbalanced,
			userAuth.currentClientId ?? "",
		],
		queryFn: async () => {
			const accessToken = await getTokenSilently();
			// getMeanComparisonForAucAsync already returns data.data (unwrapped)
			const res = (await getMeanComparisonForAucAsync(
				aucRequestData,
				userAuth.currentClientId ?? "",
				committed.meanComparisonOptionId!,
				committed.alphaOptionId!,
				isDataUnbalanced,
				accessToken,
			)) as MeanComparisonDataForAuc;
			const allLettersAreA = _.every(res.letteringPairs, (l) => l.letter === "a") && res.letteringPairs.length > 0;
			if (allLettersAreA) _.map(res.letteringPairs, (d) => (d.letter = "-"));
			return res;
		},
		enabled: canRunStats,
	});

	useEffect(() => {
		if (aucComparisonError) {
			console.error(aucComparisonError);
			toast.error("Could not load mean comparison auc data");
		}
	}, [aucComparisonError]);

	const loadingStats = isFetchingTimecourseComparison || isFetchingAucComparison;

	return (
		<Segment basic style={{ marginTop: 0, padding: 0 }}>
			<AnalysisTopBar options={trialOptions} />
			<Message error hidden={timecourseComparisonStatus === 200 || isFetchingTimecourseComparison}>
				<Message.Content>
					Repeated Measures ANOVA failed, try selecting more flights/treatments or{" "}
					<a
						style={{ cursor: "pointer" }}
						onClick={() => {
							changeSettings({ anovaType: "one-way" });
						}}
					>
						switching to Per Flight Date ANOVA
					</a>
					.
				</Message.Content>
			</Message>
			<div className="aucTableContainer">
				<AreaUnderTheCurveTableNew
					aucData={analysisData?.aucData}
					statsData={timecourseComparisonResult?.flightStats}
					flights={orderedFlights}
					gduList={gduList}
					analysisName={analysisName}
					loadingStats={loadingStats}
					loadingTable={dataLoading}
					timecoursePValue={timecourseComparisonResult?.pValue}
					timecourseMeanComparisons={timecourseComparisonResult?.letters}
					timecourseAnovaTableRows={timecourseComparisonResult?.anovaTable}
					aucMeanComparisons={aucComparisonResult}
					trialData={trialDataResults}
				/>
			</div>
			<Grid>
				<Grid.Row>
					{loadingStats || dataLoading ? (
						<Loader active size="small" />
					) : analysisData?.aucData ? (
						<Grid.Column width="8">
							<TimecourseLineChart
								aucData={analysisData?.aucData}
								analysisName={analysisName}
								gduList={gduList}
								flights={orderedFlights}
							/>
						</Grid.Column>
					) : null}

					{!loadingStats &&
						!dataLoading &&
						!!analysisData?.aucData.length &&
						(!analysisData?.aucData ? (
							<Loader active />
						) : (
							<Grid.Column width="8">
								<TimecourseBoxWhiskerChart
									aucData={analysisData?.aucData}
									analysisName={analysisName}
									aucMeanComparisons={aucComparisonResult}
								/>
							</Grid.Column>
						))}
				</Grid.Row>
			</Grid>
		</Segment>
	);
};

export default TimecourseComponentNew;
